import { Duration } from '../types';
import { MoneyType } from '../PriceMatrix/types';
import { PassengerState } from '../Checkout/store/passengers/reducers';
import {
	FlightSeatMapFragment,
	FlightSeatMapRowFragment,
	FlightSeatMapSegmentFragment,
	FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	OrderAdditionalServicesFragment,
	RowSeatFragment,
	SunSides,
	FlightStaticInfoFragment,
	SeatMapRestrictions
} from '@websky/graphql';

export enum Gender {
	Male,
	Female
}

export enum CombinedDirection {
	Left = 'left',
	Right = 'right'
}

export type SeatPositionInRow = 'first' | 'last';
export type EdgeSeatsNumbers = Map<string, SeatPositionInRow>;

export type Aircraft = FlightSeatMapSegmentFragment['segmentsInfo']['aircraft'];
export type SeatService = RowSeatFragment['seatService'];

export type GdsSeatService = FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment;

export type SeatSelectHandler = (passengerId: string, seat: Seat[], segmentId: number) => void;

export type GetRelatedSeatService = (
	services: OrderAdditionalServicesFragment,
	seatService: RowSeatFragment['seatServices']
) => GdsSeatService;

export interface CombinedWith {
	[key: string]: CombinedDirection;
}

export type Seat = RowSeatFragment & {
	services: GdsSeatService[];
	service: GdsSeatService; // for compatibility
	combinedWith?: CombinedWith;
};

export interface RowPart {
	seats: Seat[];
	service: {
		isAisle?: boolean;
		minPrice?: MoneyType;
		seatService?: RowSeatFragment['seatService'];
	};
}

export type RowWithParts = FlightSeatMapRowFragment & {
	parts: RowPart[];
	seats?: Seat[];
};

export type Equals<X, Y> = [X] extends [Y] ? ([Y] extends [X] ? true : false) : false;

export type ReplaceNestedType<OriginalType, CompareWith, ReplaceWith> = {
	[Key in keyof OriginalType]: Equals<OriginalType[Key], CompareWith> extends true
		? ReplaceWith
		: ReplaceNestedType<OriginalType[Key], CompareWith, ReplaceWith>;
};

export type SeatMapRow = Omit<FlightSeatMapRowFragment, 'seats'> & {
	seats: Seat[];
};

export type SeatMapDeck = Omit<FlightSeatMapSegmentFragment['decks'][0], 'rows'> & {
	rows: SeatMapRow[];
};

export type SeatMapSegment = Omit<FlightSeatMapSegmentFragment, 'decks'> & {
	decks: SeatMapDeck[];
};

export type SeatMapWithSeatsWithService = {
	segments: SeatMapSegment[];
	additionalServices: OrderAdditionalServicesFragment;
};

export type SeatMapDeckWithRowParts = Omit<SeatMapDeck, 'rows'> & {
	rows: RowWithParts[];
};

export type SeatMapSegmentWithRowParts = Omit<SeatMapSegment, 'decks'> & {
	decks: SeatMapDeckWithRowParts[];
};

export type SeatMapWithRowParts = {
	segments: SeatMapSegmentWithRowParts[];
	additionalServices: OrderAdditionalServicesFragment;
};

export interface Segment {
	services: GdsSeatService[];
	isSeatsAvailableInCheckinOnly: boolean;
	sunSide: SunSides;
	segmentInfo: FlightSeatMapSegmentFragment['segmentsInfo'] & { duration?: Duration };
	decks: SeatMapDeckWithRowParts[];
	amenities: FlightStaticInfoFragment['amenities'];
}

export interface ServicesMap {
	[segmentId: string]: {
		[passengerId: string]: {
			[serviceId: string]: GdsSeatService;
		};
	};
}

export interface SeatMapCallbacks {
	setSeatCallback: (seats: Seat[], passengerId: string, segmentId: string) => void;
	setClearSeatCallback: (seats: Seat[], passengerId: string, segmentId: string) => void;
	onSegmentClear: (segmentId?: number) => void;
	onPassengerClear: (passengerId: string) => void;
}

export type AllowedSeatMapFilters = {
	title: SeatMapRestrictions;
	isEffective: boolean;
};

export interface PassengerFiltersData {
	allowedFilters: SeatMapRestrictions[];
	availableSeats: Seat[];
}

export interface BuildPassengerSegmentsOptions {
	seatMaps: FlightSeatMapFragment;
	additionalServices: OrderAdditionalServicesFragment;
	passengers: PassengerState[];
	passenger: PassengerState;
	segmentIndex: number;
	amenities?: FlightStaticInfoFragment['amenities'];
	isNewOrder?: boolean;
}

export type PassengerFiltersContext = Map<string, PassengerFiltersData>;
